import { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import type { Heading } from './markdown';

type Props = {
  headings: ReadonlyArray<Heading>;
};

export default function BlogPostTocV2({ headings }: Props) {
  const [active, setActive] = useState<string>(headings[0]?.id ?? '');

  useEffect(() => {
    if (!headings.length) return;
    const els = headings
      .map((h) => document.getElementById(h.id))
      .filter((el): el is HTMLElement => el !== null);
    if (!els.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) setActive(visible.target.id);
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] },
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [headings]);

  if (!headings.length) return null;

  return (
    <nav aria-label="Sommaire" className="sticky top-8 self-start">
      <div className="border-2 border-charcoal-800 bg-white p-5 shadow-neobrutal-4">
        <div className="mb-4 flex items-center gap-3 font-marketing-mono text-[11px] font-medium uppercase tracking-[0.14em] text-charcoal-500">
          <span className="h-px w-7 bg-lime-500" />
          Sommaire
        </div>
        <ul className="space-y-2.5">
          {headings.map((h) => {
            const isActive = active === h.id;
            return (
              <li key={h.id}>
                <a
                  href={`#${h.id}`}
                  className={clsx(
                    'block border-l-2 py-1 pl-3 font-marketing-body text-[14px] leading-snug transition-colors',
                    h.level === 3 && 'pl-6 text-[13px]',
                    isActive
                      ? 'border-lime-500 font-semibold text-charcoal-800'
                      : 'border-transparent text-charcoal-600 hover:border-charcoal-300 hover:text-charcoal-800',
                  )}
                >
                  {h.text}
                </a>
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
}
